import type { TableRowDefinition } from "$components/table";
import { parseISO } from "date-fns";
import { format, utcToZonedTime } from "date-fns-tz";
import type { IntlShape } from "react-intl";
import type { AncEncounter } from "../../../../__generated__/base.gql";
import { CellWithSubscript } from "../../_lib/cell-with-subscript";
import { getAge } from "../../_lib/get-age";
import { isDefined } from "../../_lib/is-defined";
import { AncStatusBadge } from "./anc-status-badge";

const formatIptpDose = (count: number): string => {
  if (count <= 0) return "-";
  if (count >= 4) return "≥4";
  return String(count);
};

export function createRowFormatter(
  timeZone: string,
  formatMessage: IntlShape["formatMessage"],
  iptpDoseByPatient: ReadonlyMap<string, number> = new Map(),
) {
  return (record: AncEncounter): TableRowDefinition => {
    const date = utcToZonedTime(parseISO(record.createdAt), timeZone);

    const counselling = {
      earlyInitiationOfBreastfeeding: {
        include: !!record.earlyInitiationOfBreastfeeding,
        value: formatMessage({
          defaultMessage: "Early initiation of breast feeding",
          id: "ancRegister.counselling.earlyInitiationOfBreastfeeding",
        }),
      },
      exclusiveBreastfeeding: {
        include: !!record.exclusiveBreastfeeding,
        value: formatMessage({
          defaultMessage: "Exclusive breast feeding",
          id: "ancRegister.counselling.exclusiveBreastfeeding",
        }),
      },
      femaleGenitalMutilation: {
        include: !!record.femaleGenitalMutilation,
        value: formatMessage({
          defaultMessage: "Female genital mutilation",
          id: "ancRegister.counselling.femaleGenitalMutilation",
        }),
      },
      hivTesting: {
        include: !!record.hivTesting,
        value: formatMessage({
          defaultMessage: "HIV testing",
          id: "ancRegister.counselling.hivTesting",
        }),
      },
      maternalNutrition: {
        include: !!record.maternalNutrition,
        value: formatMessage({
          defaultMessage: "Maternal nutrition",
          id: "ancRegister.counselling.maternalNutrition",
        }),
      },
    };

    const prescriptions = record.prescriptions?.filter(isDefined);
    const investigations = record.investigations
      ?.filter(isDefined)
      .filter((i) => !!i.outcome);

    return {
      id: record.id,
      patientUuid: record.patient?.uuid,
      status: <AncStatusBadge status={record.status} />,
      createdAt: CellWithSubscript({
        script: format(date, "dd MMM", { timeZone }),
        subscript: format(date, "yyyy", { timeZone }),
      }),
      expectedDueDate: record.dueDate
        ? CellWithSubscript({
            script: format(parseISO(record.dueDate), "dd MMM", { timeZone }),
            subscript: format(parseISO(record.dueDate), "yyyy", { timeZone }),
          })
        : "-",
      name: record.patient?.person.name.fullname,
      cardNumber:
        record.patient?.cardId ?? record.patient?.passport?.uid ?? "-",
      age: getAge(
        { date: record.createdAt, dateOfBirth: record.patient?.person?.dob },
        formatMessage,
      ),
      parity: formatMessage(
        {
          id: "ancRegister.parity",
          defaultMessage: "{deliveries} births, {miscarriages} miscarriages",
        },
        {
          deliveries: record.parityDeliveries ?? 0,
          miscarriages: record.parityMiscarriages ?? 0,
        },
      ),
      lmp: record.lastMenstrualPeriod
        ? CellWithSubscript({
            script: format(parseISO(record.lastMenstrualPeriod), "dd MMM", {
              timeZone,
            }),
            subscript: format(parseISO(record.lastMenstrualPeriod), "yyyy", {
              timeZone,
            }),
          })
        : "-",
      gestation: record.ageOfPregnancy
        ? formatMessage(
            {
              id: "ancRegister.ageOfPregnancy",
              defaultMessage: "{age} weeks",
            },
            {
              age: record.ageOfPregnancy ?? 0,
            },
          )
        : "-",
      visitNumber: record.visitNumber ?? "-",
      counselling:
        Object.values(counselling)
          .filter((c) => c.include)
          .map((c) => c.value)
          .join(", ") || "-",
      llin: record.longLastingInsecticidalNet
        ? formatMessage({
            id: "ancRegister.llin.yes",
            defaultMessage: "Yes",
          })
        : formatMessage({
            id: "ancRegister.llin.no",
            defaultMessage: "No",
          }),
      investigations: investigations?.length
        ? investigations?.map((i) => i.catalogueItem.title).join(", ")
        : "-",
      prescriptions: prescriptions?.length
        ? prescriptions.map((p) => p.clinicalProduct?.title ?? "").join(", ")
        : "-",
      iptpDose: formatIptpDose(
        iptpDoseByPatient.get(record.patient?.uuid ?? "") ?? 0,
      ),
    };
  };
}
